/**
 * @file request-list.tsx
 * @module app/modules/service-request/components/request-list
 * @description List of service requests with loading, empty, and error states. Requesters see a
 * table of their own requests; approvers see a card per request (approval-card.tsx).
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 2.1.2
 * @version 2.1.2
 */

import moment from 'moment';
import { Spinner } from 'react-bootstrap';
import { History, Inbox, Undo2 } from 'lucide-react';
import ApprovalCard from './approval-card';
import RequestOutcome, { formatRequestDate } from './request-outcome';
import RequestStatusBadge from './request-status-badge';
import { RequestFieldsDisplay } from './fields/request-fields';
import {
  ServiceRequestActionDefinition,
  ServiceRequest,
  ServiceRequestStatus,
  ServiceRequestScope,
  ServiceRequestStep,
  ServiceRequestTab,
} from '../types/service-request.types';

type RequestListProps = {
  records: ServiceRequest[];
  /** Known actions by key; rows of other actions show their inputs as plain values. */
  actionsByKey: Map<string, ServiceRequestActionDefinition>;
  loading: boolean;
  error: string | null;
  scope: ServiceRequestScope;
  tab: ServiceRequestTab;
  busyId: string | null;
  busyStep: ServiceRequestStep | null;
  canSendToJira: boolean;
  highlightId?: string | null;
  /** Rows decided after this time get a "New" marker (my History only). */
  newSince?: string | null;
  onRetry: () => void;
  onApprove: (request: ServiceRequest) => void;
  onDecline: (request: ServiceRequest) => void;
  onWithdraw: (request: ServiceRequest) => void;
  onSendToJira: (request: ServiceRequest) => void;
};

const Status = ServiceRequestStatus;

/** Statuses the requester is told about; their own withdrawal is not news to them. */
const DECIDED = [
  Status.Completed,
  Status.Failed,
  Status.Declined,
  Status.SentToJira,
];

const isNewDecision = (request: ServiceRequest, since?: string | null) =>
  Boolean(since) &&
  DECIDED.includes(request.status) &&
  new Date(request.statusUpdatedAt || 0).getTime() >
    new Date(since as string).getTime();

const EMPTY_COPY: Record<
  ServiceRequestScope,
  Record<ServiceRequestTab, { title: string; text: string }>
> = {
  mine: {
    pending: {
      title: 'No pending requests',
      text: 'Requests you raise wait here until an approver decides on them.',
    },
    history: {
      title: 'No past requests yet',
      text: 'Approved, declined, failed, and withdrawn requests move here, with who decided and why.',
    },
  },
  approvals: {
    pending: {
      title: 'Nothing to approve',
      text: 'You are all caught up. New requests from your team appear here.',
    },
    history: {
      title: 'No decisions yet',
      text: 'Requests that have been approved or declined appear here.',
    },
  },
};

const LoadingRows = () => (
  <div className="sr-skeleton" aria-busy="true" aria-label="Loading requests">
    {[0, 1, 2].map((row) => (
      <div key={row} className="sr-skeleton__row placeholder-glow">
        <span className="placeholder col-2" />
        <span className="placeholder col-3" />
        <span className="placeholder col-2" />
        <span className="placeholder col-1" />
      </div>
    ))}
  </div>
);

const RequestList = ({
  records,
  actionsByKey,
  loading,
  error,
  scope,
  tab,
  busyId,
  busyStep,
  canSendToJira,
  highlightId,
  newSince,
  onRetry,
  onApprove,
  onDecline,
  onWithdraw,
  onSendToJira,
}: RequestListProps) => {
  // On the Pending tab requesters can withdraw.
  const showActions = tab === 'pending';

  if (loading && !records.length) return <LoadingRows />;

  if (error) {
    return (
      <div
        className="sr-callout sr-callout--danger sr-callout--spread"
        role="alert"
      >
        <span>{error}</span>
        <button
          type="button"
          className="uw-btn uw-btn-light btn btn-sm btn-light"
          onClick={onRetry}
        >
          Try again
        </button>
      </div>
    );
  }

  if (!records.length) {
    const copy = EMPTY_COPY[scope][tab];
    const EmptyIcon = tab === 'pending' ? Inbox : History;
    return (
      <div className="sr-empty">
        <span className="sr-empty__icon">
          <EmptyIcon size={28} aria-hidden="true" />
        </span>
        <div className="sr-empty__title">{copy.title}</div>
        <p className="sr-empty__text">{copy.text}</p>
      </div>
    );
  }

  if (scope === 'approvals') {
    return (
      <ul
        className={`sr-approval-list${loading ? ' is-refreshing' : ''}`}
        aria-label="Requests"
      >
        {records.map((request) => (
          <li key={request._id}>
            <ApprovalCard
              request={request}
              fields={actionsByKey.get(request.action)?.fields}
              busyStep={busyId === request._id ? busyStep : null}
              disabled={Boolean(busyId)}
              canSendToJira={canSendToJira}
              onApprove={onApprove}
              onDecline={onDecline}
              onSendToJira={onSendToJira}
            />
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div
      className={`table-responsive sr-table-wrap${
        loading ? ' is-refreshing' : ''
      }`}
    >
      <table className="table align-middle sr-table">
        <thead>
          <tr>
            <th scope="col">Request</th>
            <th scope="col">Details</th>
            <th scope="col">Raised</th>
            <th scope="col">Status</th>
            {showActions ? (
              <th scope="col" className="text-end">
                Actions
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {records.map((request) => {
            const busy = busyId === request._id;
            return (
              <tr
                key={request._id}
                className={
                  request._id === highlightId ? 'sr-row--new' : undefined
                }
              >
                <td className="sr-col-request">
                  <div className="sr-request-id">
                    {request.refKey}
                    {isNewDecision(request, newSince) ? (
                      <span className="sr-new-pill">New</span>
                    ) : null}
                  </div>
                  <div className="sr-muted">{request.actionLabel}</div>
                  {request.requestReason ? (
                    <div className="sr-note" title={request.requestReason}>
                      {request.requestReason}
                    </div>
                  ) : null}
                </td>
                <td className="sr-col-details">
                  <RequestFieldsDisplay
                    payload={request.input}
                    fields={actionsByKey.get(request.action)?.fields}
                  />
                </td>
                <td className="sr-col-raised">
                  <div>{formatRequestDate(request.createdAt)}</div>
                  <div className="sr-muted">
                    {moment(request.createdAt).fromNow()}
                  </div>
                </td>
                <td className="sr-col-status">
                  <RequestStatusBadge status={request.status} />
                  <RequestOutcome request={request} />
                </td>
                {showActions ? (
                  <td className="text-end sr-col-actions">
                    {request.status === Status.Pending ? (
                      <button
                        type="button"
                        className="uw-btn uw-btn-light btn btn-sm btn-light"
                        disabled={Boolean(busyId)}
                        aria-label={`Withdraw ${request.refKey}`}
                        onClick={() => onWithdraw(request)}
                      >
                        {busy ? (
                          <Spinner
                            animation="border"
                            size="sm"
                            aria-hidden="true"
                          />
                        ) : (
                          <Undo2 size={16} aria-hidden="true" />
                        )}
                        <span className="ms-1">Withdraw</span>
                      </button>
                    ) : null}
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default RequestList;
